"use client";

import { useState } from "react";

interface ConnectAccountPanelProps {
  onConnect: (username: string) => Promise<void>;
  loading?: boolean;
  error?: string | null;
}

export default function ConnectAccountPanel({
  onConnect,
  loading = false,
  error = null,
}: ConnectAccountPanelProps) {
  const [username, setUsername] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = username.trim();
    if (!trimmed || loading) return;
    await onConnect(trimmed);
  };

  return (
    <div className="flex w-full flex-col items-center justify-start lg:min-h-[min(70vh,640px)] lg:justify-center">
      <div className="w-full max-w-lg rounded-2xl border border-line bg-surface p-6 shadow-card sm:p-8">
        <p className="text-[12px] font-semibold uppercase tracking-widest text-rose-deep">
          Add account
        </p>
        <h2 className="mt-2 font-display text-[22px] font-bold leading-tight text-ink sm:text-[24px]">
          Track a public Instagram account
        </h2>
        <p className="mt-2 text-[14px] leading-relaxed text-muted">
          Enter the username of any public profile. We&apos;ll verify it&apos;s accessible before
          starting your first scrape.
        </p>

        <form onSubmit={submit} className="mt-6">
          <label htmlFor="connect-username" className="sr-only">
            Instagram username
          </label>
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative min-w-0 flex-1">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[15px] text-muted">
                @
              </span>
              <input
                id="connect-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="username"
                disabled={loading}
                autoComplete="off"
                spellCheck={false}
                className="w-full rounded-xl border border-line bg-bg py-3 pl-9 pr-4 text-[15px] text-ink outline-none transition focus:border-rose/50"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !username.trim()}
              className="shrink-0 rounded-xl grad-bg px-5 py-3 text-[14px] font-semibold text-white transition disabled:opacity-50"
            >
              {loading ? "Analyzing…" : "Analyze"}
            </button>
          </div>
        </form>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] leading-relaxed text-red-700">
            {error}
          </div>
        )}

        <p className="mt-5 text-[12px] leading-relaxed text-muted">
          Private accounts cannot be tracked. Peeqd reads public profile data only.
        </p>
      </div>
    </div>
  );
}
